import React, { useState } from 'react';
import { MapPin, X, Calendar, Ticket, Maximize2, Minimize2, RefreshCw, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = '';

export const StadiumPlanModal = ({ open, onOpenChange, stadium, matchInfo }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentMapUrl, setCurrentMapUrl] = useState(null);
  
  if (!open || !stadium) return null;

  const mapUrl = currentMapUrl || stadium.mapUrl;

  const handleClose = () => {
    setIsFullscreen(false);
    setEditMode(false);
    setNewUrl('');
    setCurrentMapUrl(null);
    onOpenChange(false);
  };

  const handleSaveUrl = async () => {
    if (!newUrl.trim()) {
      toast.error('Collez une URL d\'image');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/stadiums/update-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stadium_id: stadium.id, map_url: newUrl.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setCurrentMapUrl(newUrl.trim());
        stadium.mapUrl = newUrl.trim();
        setEditMode(false);
        setNewUrl('');
        toast.success('Plan mis à jour !');
      } else {
        toast.error(data.message || 'Erreur');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Mode plein écran
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="stadium-plan-fullscreen">
        <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/80 border-b border-zinc-700">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-[#C4A052] flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {stadium.name}
            </h2>
            {matchInfo?.matchNum && (
              <span className="bg-[#C4A052] text-black font-bold px-2 py-0.5 rounded text-sm">
                {matchInfo.matchNum}
              </span>
            )}
            <span className="text-sm text-zinc-300">
              {matchInfo?.eventName || ''} {matchInfo?.date && `• ${matchInfo.date}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsFullscreen(false)}
              variant="outline"
              size="sm"
              className="border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/10"
              data-testid="minimize-btn"
            >
              <Minimize2 className="h-4 w-4 mr-1" />
              Réduire
            </Button>
            <Button onClick={handleClose} variant="ghost" size="sm" data-testid="close-fullscreen-btn">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
          <img
            src={mapUrl}
            alt={`Plan du stade ${stadium.name}`}
            className="max-w-full max-h-full object-contain"
            data-testid="stadium-plan-image"
          />
        </div>
        
        <div className="px-4 py-2 bg-zinc-900/80 border-t border-zinc-700 flex items-center justify-center gap-4">
          <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#C4A052] hover:underline">
            Ouvrir l'image dans un nouvel onglet
          </a>
          <Button
            onClick={() => { setIsFullscreen(false); setEditMode(true); }}
            variant="ghost"
            size="sm"
            className="text-zinc-400 hover:text-[#C4A052]"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Changer le plan
          </Button>
        </div>
      </div>
    );
  }

  // Mode normal
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="stadium-plan-modal">
      <div className="absolute inset-0 bg-black/90" onClick={handleClose} />
      
      <div className="relative z-10 w-[95vw] max-w-7xl bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 bg-zinc-900">
          <h2 className="text-lg font-bold text-[#C4A052] flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Plan du Stade
          </h2>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsFullscreen(true)}
              variant="outline"
              size="sm"
              className="border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/10"
              data-testid="fullscreen-btn"
            >
              <Maximize2 className="h-4 w-4 mr-1" />
              Plein écran
            </Button>
            <Button onClick={handleClose} variant="ghost" size="sm" data-testid="close-modal-btn">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Info Bar */}
        <div className="flex items-center justify-between gap-4 mx-4 my-3 bg-zinc-800 rounded-lg px-4 py-2 border border-zinc-700">
          <div className="flex items-center gap-3">
            {matchInfo?.matchNum && (
              <span className="bg-[#C4A052] text-black font-bold px-2 py-1 rounded text-sm">
                {matchInfo.matchNum}
              </span>
            )}
            <div>
              <p className="text-sm font-semibold text-white">
                {matchInfo?.eventName || stadium.name}
              </p>
              <p className="text-xs text-zinc-400">
                {matchInfo?.date && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {matchInfo.date}
                  </span>
                )}
                {matchInfo?.categorie && (
                  <span className="ml-3 text-[#C4A052] inline-flex items-center gap-1">
                    <Ticket className="h-3 w-3" />
                    {matchInfo.categorie}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold text-[#C4A052]">{stadium.name}</p>
            <p className="text-xs text-zinc-400">{stadium.city} • <span className="text-[#C4A052]">{(stadium.capacity || 0).toLocaleString()}</span> places</p>
          </div>
        </div>

        {/* Plan Image */}
        <div className="px-4 pb-3 flex items-center justify-center bg-zinc-800 mx-4 rounded-lg border border-zinc-700">
          <img
            src={mapUrl}
            alt={`Plan du stade ${stadium.name}`}
            className="w-full h-auto max-h-[70vh] object-contain"
            data-testid="stadium-plan-image"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>

        {/* Edit mode */}
        {editMode && (
          <div className="mx-4 mt-3 bg-zinc-800 rounded-lg p-3 border border-[#C4A052]/30" data-testid="edit-plan-section">
            <p className="text-sm text-[#C4A052] font-medium mb-2">Mettre à jour le plan de {stadium.name}</p>
            
            {/* Upload image */}
            <div className="flex gap-2 mb-2">
              <input
                type="file"
                accept="image/*"
                id="plan-upload"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setSaving(true);
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const res = await fetch(`${API_URL}/api/stadiums/upload-plan?stadium_id=${stadium.id}`, {
                      method: 'POST',
                      body: formData
                    });
                    const data = await res.json();
                    if (data.success) {
                      setCurrentMapUrl(data.map_url);
                      stadium.mapUrl = data.map_url;
                      setEditMode(false);
                      toast.success('Plan uploadé !');
                    } else {
                      toast.error(data.message || 'Erreur');
                    }
                  } catch (err) {
                    toast.error('Erreur upload');
                  } finally {
                    setSaving(false);
                  }
                }}
              />
              <Button
                onClick={() => document.getElementById('plan-upload')?.click()}
                disabled={saving}
                size="sm"
                className="bg-[#C4A052] hover:bg-[#D4B86A] text-black font-semibold"
                data-testid="upload-plan-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Insérer une image
              </Button>
              <span className="text-xs text-zinc-500 self-center">ou collez une URL ci-dessous</span>
            </div>

            {/* URL input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="Collez l'URL de la nouvelle image du plan..."
                className="flex-1 bg-zinc-900 border border-zinc-600 text-white text-sm rounded px-3 py-2 focus:border-[#C4A052] focus:outline-none"
                data-testid="new-plan-url-input"
              />
              <Button
                onClick={handleSaveUrl}
                disabled={saving || !newUrl.trim()}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="save-plan-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> OK</>}
              </Button>
              <Button
                onClick={() => { setEditMode(false); setNewUrl(''); }}
                variant="ghost"
                size="sm"
                className="text-zinc-400"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {newUrl.trim() && (
              <div className="mt-2">
                <p className="text-xs text-zinc-500 mb-1">Aperçu :</p>
                <img
                  src={newUrl}
                  alt="Aperçu"
                  className="max-h-[150px] rounded border border-zinc-600"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  onLoad={(e) => { e.target.style.display = 'block'; }}
                />
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-3 flex items-center justify-center gap-4 border-t border-zinc-700">
          <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#C4A052] hover:underline">
            Ouvrir l'image dans un nouvel onglet
          </a>
          {!editMode && (
            <Button
              onClick={() => setEditMode(true)}
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-[#C4A052]"
              data-testid="change-plan-btn"
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Changer le plan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StadiumPlanModal;
